import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";

export const InquiryForm = ({ vehicleId, vehicleLabel }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", financing: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/vehicles/${vehicleId}/inquire`, form);
      setDone(true);
      toast.success("Inquiry sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="inquiry-success">
        <CheckCircle2 className="mx-auto text-green-600 mb-3" size={40} />
        <h3 className="font-display font-bold text-lg text-slate-900">Thanks, {form.name.split(" ")[0]}!</h3>
        <p className="text-sm text-slate-600 mt-1">
          Your inquiry about the {vehicleLabel} was sent. We'll get back to you soon.
        </p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Full name</Label>
          <Input required value={form.name} onChange={set("name")} className="mt-1" placeholder="John Smith" data-testid="inquiry-name-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Phone</Label>
          <Input required value={form.phone} onChange={set("phone")} className="mt-1" placeholder="(506) 000-0000" data-testid="inquiry-phone-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Email</Label>
        <Input required type="email" value={form.email} onChange={set("email")} className="mt-1" placeholder="you@email.com" data-testid="inquiry-email-input" />
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Message</Label>
        <Textarea required value={form.message} onChange={set("message")} className="mt-1" rows={4}
          placeholder={`I'm interested in the ${vehicleLabel}. Is it still available?`} data-testid="inquiry-message-input" />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={form.financing} onCheckedChange={(v) => setForm({ ...form, financing: !!v })} data-testid="inquiry-financing-checkbox" />
        <span className="text-sm text-slate-600">I'm interested in financing options</span>
      </label>
      <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-12 rounded-full active:scale-95" data-testid="inquiry-submit-button">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} className="mr-2" /> Send Inquiry</>}
      </Button>
    </form>
  );
};
